<script setup>
import { useApi } from '@/utils/api'

definePage({
  meta: {
    layout: 'default',
    action: 'read',
    subject: 'StockMovement',
  },
})

// ─── State ──────────────────────────────────────────────────────────────────
const movements = ref([])
const totalItems = ref(0)
const isFetching = ref(false)

const options = ref({ page: 1, itemsPerPage: 20 })

const filters = ref({
  type: null,
  date_from: null,
  date_to: null,
})

const typeOptions = [
  { title: 'Semua Tipe', value: null },
  { title: 'Pembelian', value: 'purchase' },
  { title: 'Penjualan', value: 'sale' },
  { title: 'Adjustment', value: 'adjustment' },
  { title: 'Stock Opname', value: 'opname' },
  { title: 'Void Penjualan', value: 'void_sale' },
  { title: 'Stok Awal', value: 'initial' },
]

const headers = [
  { title: 'Tanggal', key: 'created_at', width: '160px' },
  { title: 'Bahan Baku', key: 'ingredient', sortable: false },
  { title: 'Tipe', key: 'type' },
  { title: 'Qty', key: 'quantity', align: 'end' },
  { title: 'Stok Sebelum', key: 'stock_before', align: 'end' },
  { title: 'Stok Sesudah', key: 'stock_after', align: 'end' },
  { title: 'Dibuat Oleh', key: 'created_by', sortable: false },
  { title: 'Keterangan', key: 'notes', sortable: false },
]

// ─── Fetch ──────────────────────────────────────────────────────────────────
const fetchMovements = async () => {
  isFetching.value = true
  try {
    const params = new URLSearchParams({
      page: options.value.page,
      per_page: options.value.itemsPerPage,
    })
    if (filters.value.type) params.set('type', filters.value.type)
    if (filters.value.date_from) params.set('date_from', filters.value.date_from)
    if (filters.value.date_to) params.set('date_to', filters.value.date_to)

    const response = await useApi(`/api/v1/stock-movements?${params}`).get()
    const data = response?.data ?? response

    movements.value = data?.data ?? []
    totalItems.value = data?.pagination?.total ?? 0  // ← backend pakai 'pagination'
  } catch (err) {
    console.error('❌ fetchMovements:', err)
  } finally {
    isFetching.value = false
  }
}

watch([options, filters], fetchMovements, { deep: true })
onMounted(fetchMovements)

// ─── Helpers ────────────────────────────────────────────────────────────────
const typeColor = (type) => ({
  purchase: 'success',
  sale: 'error',
  adjustment: 'warning',
  opname: 'info',
  void_sale: 'secondary',
  initial: 'primary',
}[type] ?? 'default')

const typeLabel = (type) => ({
  purchase: 'Pembelian',
  sale: 'Penjualan',
  adjustment: 'Adjustment',
  opname: 'Stock Opname',
  void_sale: 'Void Penjualan',
  initial: 'Stok Awal',
}[type] ?? type)

const formatDateTime = (date) => {
  if (!date) return '-'
  return new Date(date).toLocaleString('id-ID', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

const truncate = (text, length) => {
  if (!text) return ''
  return text.length > length ? text.substring(0, length) + '...' : text
}
</script>

<template>
  <VRow>
    <VCol cols="12">
      <div class="d-flex align-center mb-6">
        <div>
          <h4 class="text-h4 font-weight-bold">Riwayat Pergerakan Stok</h4>
          <p class="text-body-2 text-medium-emphasis mt-1">Ledger immutable — tidak dapat diubah</p>
        </div>
        <VSpacer />
        <VBtn variant="tonal" prepend-icon="ri-refresh-line" :loading="isFetching" @click="fetchMovements">
          Refresh
        </VBtn>
      </div>

      <!-- Filters -->
      <VCard class="mb-4">
        <VCardText class="py-3">
          <VRow>
            <VCol cols="12" md="3">
              <VSelect
                v-model="filters.type"
                :items="typeOptions"
                label="Tipe Movement"
                density="compact"
                variant="outlined"
                hide-details
                clearable
              />
            </VCol>
            <VCol cols="12" md="3">
              <VTextField
                v-model="filters.date_from"
                label="Dari Tanggal"
                type="date"
                density="compact"
                variant="outlined"
                hide-details
                clearable
              />
            </VCol>
            <VCol cols="12" md="3">
              <VTextField
                v-model="filters.date_to"
                label="Sampai Tanggal"
                type="date"
                density="compact"
                variant="outlined"
                hide-details
                clearable
              />
            </VCol>
          </VRow>
        </VCardText>
      </VCard>

      <!-- Table -->
      <VCard>
        <VDataTableServer
          v-model:options="options"
          :headers="headers"
          :items="movements"
          :items-length="totalItems"
          :loading="isFetching"
          :items-per-page-options="[20, 50, 100]"
          class="text-no-wrap"
        >
          <template #item.created_at="{ item }">
            <span class="text-body-2">{{ formatDateTime(item.created_at) }}</span>
          </template>
          <template #item.ingredient="{ item }">
            <span class="font-weight-medium">{{ item.ingredient?.name ?? '-' }}</span>
            <span v-if="item.ingredient?.unit" class="text-caption text-medium-emphasis ms-1">
              ({{ item.ingredient.unit }})
            </span>
          </template>
          <template #item.type="{ item }">
            <VChip :color="typeColor(item.type)" size="small" label>
              {{ typeLabel(item.type) }}
            </VChip>
          </template>
          <template #item.quantity="{ item }">
            <span :class="item.quantity > 0 ? 'text-success' : 'text-error'" class="font-weight-medium">
              {{ item.quantity > 0 ? '+' : '' }}{{ item.quantity }}
            </span>
          </template>
          <template #item.stock_before="{ item }">{{ item.stock_before }}</template>
          <template #item.stock_after="{ item }">
            <span class="font-weight-medium">{{ item.stock_after }}</span>
          </template>
          <template #item.created_by="{ item }">
            <span class="text-body-2">{{ item.created_by?.name ?? '-' }}</span>
          </template>
          <template #item.notes="{ item }">
            <span class="text-body-2 text-medium-emphasis">{{ truncate(item.notes, 40) || '-' }}</span>
          </template>
          <template #no-data>
            <div class="text-center py-8">
              <VIcon size="56" color="secondary" icon="ri-archive-line" class="mb-3" />
              <p class="text-body-1 text-medium-emphasis">Belum ada riwayat pergerakan stok</p>
              <p class="text-body-2 text-disabled">Coba ubah filter untuk hasil yang berbeda</p>
            </div>
          </template>
        </VDataTableServer>
      </VCard>
    </VCol>
  </VRow>
</template>
